import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { titleSuffix } from "../utils/brand"

// The page chrome survives transcript replacements while a reply streams.
export default class extends Controller {
    static targets = ["title", "toc", "marks", "preview", "previewPrompt", "previewReply", "status"]
    static values = { conversationId: Number, outline: Array, titlePending: Boolean, titleUrl: String }

    connect() {
        this.scroller = this.element.querySelector(".assistant-content")
        if (!this.scroller || !this.hasMarksTarget) return
        this.entries = new Map(this.outlineValue.map((entry) => [String(entry.id), entry]))
        this._refresh = this._refresh.bind(this)
        this._onScroll = () => {
            if (this._scrollFrame) return
            this._scrollFrame = requestAnimationFrame(() => {
                this._scrollFrame = null
                this._updateActive()
            })
        }
        this.scroller.addEventListener("scroll", this._onScroll, { passive: true })
        this.observer = new MutationObserver(this._refresh)
        this.observer.observe(this.scroller, { childList: true, subtree: true, characterData: true })
        this._refresh()
        this.consumer = createConsumer()
        this.subscription = this.consumer.subscriptions.create({ channel: "AssistantActivityChannel" }, {
            received: (data) => this._receive(data),
            connected: () => this._syncTitle()
        })
    }

    disconnect() {
        this.observer?.disconnect()
        this.scroller?.removeEventListener("scroll", this._onScroll)
        if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame)
        this.subscription?.unsubscribe()
        this.consumer?.disconnect()
    }

    // Close the small gap between the initial render and subscribing: a fast
    // title may have been saved before this page joined the live stream.
    async _syncTitle() {
        if (!this.titlePendingValue || !this.conversationIdValue) return
        try {
            const url = new URL(this.titleUrlValue, location.origin)
            url.searchParams.set("conversation_id", this.conversationIdValue)
            const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            if (!response.ok) return
            const data = await response.json()
            if (this.element.isConnected) this._receive({ type: "conversation_title", conversation_id: this.conversationIdValue, title: data.title })
        } catch (_error) { /* The saved title will still appear on the next visit. */ }
    }

    _receive(data) {
        if (data.type !== "conversation_title" || Number(data.conversation_id) !== this.conversationIdValue) return
        if (!data.title || data.title === "Assistant") return
        if (this.hasTitleTarget) {
            this.titleTarget.textContent = data.title
            const trigger = this.titleTarget.closest("button")
            if (trigger) trigger.title = data.title
        }
        this.element.querySelectorAll('.jane-chat-page-menu .chat-title-editor:not([open]) input[name="title"]').forEach((input) => {
            input.value = data.title
        })
        document.title = `${data.title} - ${titleSuffix()}`
        document.querySelectorAll(`.rail-chat[data-conversation-id="${this.conversationIdValue}"]`).forEach((row) => {
            const label = row.querySelector(".rail-chat-label")
            if (label) label.textContent = data.title
            const link = row.querySelector(".rail-chat-link")
            if (link) link.title = data.title
        })
    }

    _refresh() {
        let current = null
        this.scroller.querySelectorAll("[data-turn-id]").forEach((node) => {
            if (node.classList.contains("assistant-bubble-user") || node.classList.contains("assistant-turn-attachments")) {
                const id = node.dataset.turnId
                const prompt = node.classList.contains("assistant-turn-attachments") ? "Attached files" : node.textContent.trim()
                current = this.entries.get(id) || { id, prompt: prompt.slice(0, 240), reply: "" }
                this.entries.set(id, current)
            } else if (current && !current.reply && node.classList.contains("assistant-bubble-assistant") && !node.dataset.assistantPending) {
                current.reply = node.textContent.trim().slice(0, 240)
            }
        })
        const entries = Array.from(this.entries.values()).sort((a, b) => Number(a.id) - Number(b.id))
        const signature = entries.map((entry) => entry.id).join(",")
        if (signature !== this.signature) {
            this.signature = signature
            const focusedId = this.marksTarget.contains(document.activeElement) ? document.activeElement.dataset.turnId : null
            this.previewTarget.hidden = true
            this.marksTarget.replaceChildren(...entries.map((entry) => this._mark(entry)))
            if (focusedId) this.marksTarget.querySelector(`[data-turn-id="${Number(focusedId)}"]`)?.focus({ preventScroll: true })
        }
        this.tocTarget.hidden = entries.length < 2
        this._updateActive()
    }

    _mark(entry) {
        const button = document.createElement("button")
        button.type = "button"
        button.className = "chat-toc-mark"
        button.tabIndex = -1
        button.dataset.turnId = entry.id
        button.setAttribute("aria-label", `Go to: ${entry.prompt}`)
        button.addEventListener("mouseenter", () => this._preview(entry, button))
        button.addEventListener("focus", () => this._preview(entry, button))
        const hidePreview = () => { this.previewTarget.hidden = true; button.removeAttribute("aria-describedby") }
        button.addEventListener("mouseleave", hidePreview)
        button.addEventListener("blur", hidePreview)
        button.addEventListener("keydown", (event) => {
            if (event.key === "Escape") { hidePreview(); return }
            const buttons = Array.from(this.marksTarget.children)
            const index = buttons.indexOf(button)
            const next = { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: buttons.length - 1 }[event.key]
            if (next === undefined) return
            event.preventDefault()
            const selected = buttons[Math.max(0, Math.min(buttons.length - 1, next))]
            buttons.forEach((mark) => { mark.tabIndex = mark === selected ? 0 : -1 })
            selected?.focus()
        })
        button.addEventListener("click", () => this._jump(entry, button))
        return button
    }

    _preview(entry, button) {
        this.marksTarget.querySelectorAll("[aria-describedby]").forEach((mark) => mark.removeAttribute("aria-describedby"))
        this.previewPromptTarget.textContent = entry.prompt
        this.previewReplyTarget.textContent = entry.reply
        this.previewReplyTarget.hidden = !entry.reply
        this.previewTarget.hidden = false
        const offset = button.getBoundingClientRect().top - this.tocTarget.getBoundingClientRect().top
        this.previewTarget.style.top = `${Math.max(0, offset - this.previewTarget.offsetHeight / 2)}px`
        button.setAttribute("aria-describedby", "chat_turn_preview")
    }

    async _jump(entry, button) {
        if (this.jumping) return
        this.jumping = true
        this.tocTarget.setAttribute("aria-busy", "true")
        this.previewTarget.hidden = true
        try {
            const selector = `[data-turn-id="${Number(entry.id)}"]`
            let target = this.scroller.querySelector(selector)
            if (!target) {
                this.statusTarget.textContent = "Loading earlier messages…"
                const host = this.scroller.querySelector('[data-controller~="chat-history"]')
                const history = host && this.application.getControllerForElementAndIdentifier(host, "chat-history")
                target = await history?.reveal(entry.id)
            }
            target = this.scroller.querySelector(selector)
            if (!target || !this.element.isConnected) {
                this.statusTarget.textContent = "Could not load that message. Try again."
                return
            }
            const top = target.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top + this.scroller.scrollTop - 16
            this.scroller.scrollTo({ top, behavior: "instant" })
            this.statusTarget.textContent = ""
            this.marksTarget.querySelectorAll(".is-selected").forEach((mark) => mark.classList.remove("is-selected"))
            button.classList.add("is-selected")
            this._updateActive()
        } finally {
            this.jumping = false
            this.tocTarget.removeAttribute("aria-busy")
        }
    }

    _updateActive() {
        const top = this.scroller.getBoundingClientRect().top + 100
        const prompts = Array.from(this.scroller.querySelectorAll(".assistant-bubble-user[data-turn-id], .assistant-turn-attachments[data-turn-id]"))
        const current = prompts.filter((node) => node.getBoundingClientRect().top <= top).at(-1) || prompts[0]
        const focused = this.marksTarget.contains(document.activeElement) ? document.activeElement : null
        const currentId = current?.dataset.turnId || this.marksTarget.lastElementChild?.dataset.turnId
        this.marksTarget.querySelectorAll("button").forEach((button) => {
            button.tabIndex = (focused ? button === focused : button.dataset.turnId === currentId) ? 0 : -1
            if (button.dataset.turnId === currentId) button.setAttribute("aria-current", "location")
            else button.removeAttribute("aria-current")
        })
    }
}
